import { AlertCircle, Cpu } from "lucide-react";
import { Badge } from "./ui";
import { InfoTip } from "./Erklaerung";
import { MODEL_CATEGORY, MODEL_EXPLAIN, MODEL_LABEL, type LicenseModel } from "../../domain/licensing";
import { landscapeLicensing, landscapeModel, openEngineAssignments } from "../../data/licensing";

const TONE: Record<LicenseModel, "brand" | "outline" | "gray"> = {
  fue: "outline",
  "fue+engine": "brand",
  "eigene-metrik": "gray",
  keine: "gray",
};

/** Kategorie A–D als kurzes Kennzeichen. */
export function LizenzBadge({ model }: { model: LicenseModel }) {
  return (
    <Badge tone={TONE[model]}>
      {MODEL_CATEGORY[model]} · {MODEL_LABEL[model]}
    </Badge>
  );
}

/**
 * Die Lizenzeinstufung einer Landschaft: welches System welche Metrik trägt,
 * woher die Einstufung stammt und was davon noch offen ist. Nichts wird geraten —
 * unbekannte Werte stehen als „offen" da.
 */
export function LizenzBlock({ landscapeId, compact = false }: { landscapeId: string; compact?: boolean }) {
  const systeme = landscapeLicensing(landscapeId);
  if (systeme.length === 0) return null;
  const model = landscapeModel(landscapeId);

  return (
    <div className="bg-sunken mt-3 rounded-xl border-soft p-3" style={{ borderWidth: 1 }}>
      {/* compact: Die Einordnung steht auf der Karte schon darüber — nicht doppelt sagen. */}
      {!compact && (
        <div className="flex flex-wrap items-center gap-2">
          <LizenzBadge model={model} />
          <InfoTip size={12}>{MODEL_EXPLAIN[model]}</InfoTip>
        </div>
      )}

      <div className={compact ? "space-y-2" : "mt-2 space-y-2"}>
        {systeme.map((s) => (
          <div key={s.sid} className="text-xs">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-strong font-mono font-semibold">{s.sid}</span>
              <LizenzBadge model={s.model} />
              {!s.belegt && (
                <span className="text-faint inline-flex items-center gap-1">
                  <AlertCircle size={11} /> Annahme
                </span>
              )}
              <InfoTip size={12}>{s.note}{s.engine && <><br />{s.engine.herkunft}</>}</InfoTip>
            </div>
            {s.engine && (
              <div className="text-dim mt-1 flex flex-wrap items-baseline gap-x-2">
                <span className="inline-flex items-center gap-1">
                  <Cpu size={11} className="shrink-0" />
                  <span className="font-medium">{s.engine.name}</span>
                </span>
                <span className="text-faint">
                  {s.engine.einheit} ·{" "}
                  {s.engine.wert === null ? (
                    <span style={{ color: "var(--color-amp-amber)" }}>Wert offen</span>
                  ) : (
                    <span className="tnum">{s.engine.wert}</span>
                  )}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Die offene Zuordnung der Engine-Systeme. Der Kunde nennt drei Systeme auf
 * Engine-Basis, belegt zugeordnet ist bisher nur GTS = GSP.
 */
export function OffeneEngineZuordnung() {
  return (
    <div
      className="rounded-lg p-3 text-xs"
      style={{ borderWidth: 1, borderColor: "var(--color-amp-amber)", backgroundColor: "var(--surface-sunken)" }}
    >
      <div className="text-normal flex items-center gap-1.5 font-medium">
        Offene Zuordnung der Engine-Systeme
        <InfoTip size={12}>Arvato nennt drei Systeme auf Engine-Lizenz-Basis. Belegt zugeordnet ist bisher nur GTS = GSP. Die beiden anderen sind als Annahme hinterlegt und im Termin zu bestätigen.</InfoTip>
      </div>
      <ul className="mt-2 space-y-1">
        {openEngineAssignments.map((o) => (
          <li key={o.engine} className="text-dim">
            <span className="text-strong font-medium">{o.engine}</span> · {o.einheit} — {o.status}
          </li>
        ))}
      </ul>
    </div>
  );
}
